/**
 * ArUco Marker Detector
 *
 * Wrapper around js-aruco for detecting ArUco markers in camera frames.
 * Used for initial marker detection in Camera Lucida's detect-once-then-lock workflow.
 */

// Import js-aruco (Vite handles CommonJS interop)
// @ts-expect-error - js-aruco is CommonJS without types
import * as jsAruco from 'js-aruco';

// Types for js-aruco
interface JsArucoCorner {
  x: number;
  y: number;
}

interface JsArucoMarker {
  id: number;
  corners: JsArucoCorner[];
}

interface JsArucoDetector {
  detect(image: { width: number; height: number; data: Uint8ClampedArray }): JsArucoMarker[];
}

interface JsArucoPosit {
  pose(corners: JsArucoCorner[]): {
    bestError: number;
    bestRotation: number[][];
    bestTranslation: number[];
    alternativeError: number;
    alternativeRotation: number[][];
    alternativeTranslation: number[];
  };
}

// Our marker type with computed properties
export interface MarkerCorner {
  x: number;
  y: number;
}

export interface DetectedMarker {
  id: number;
  corners: MarkerCorner[]; // 4 corners: [topLeft, topRight, bottomRight, bottomLeft]
  center: { x: number; y: number };
  size: number; // Approximate marker size in pixels
  rotation: number; // Rotation angle in radians
}

export interface MarkerPose {
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number }; // Euler angles in radians
  rotationMatrix: number[][];
}

export interface ArucoDetectorConfig {
  markerSizeMm?: number; // Physical marker size in mm (for pose estimation)
  focalLength?: number; // Camera focal length in pixels (approximate)
}

export class ArucoDetector {
  private detector: JsArucoDetector;
  private posit: JsArucoPosit | null = null;
  private markerSizeMm: number;
  private focalLength: number;

  constructor(config: ArucoDetectorConfig = {}) {
    this.detector = new jsAruco.AR.Detector();
    this.markerSizeMm = config.markerSizeMm || 50; // Default 50mm marker
    this.focalLength = config.focalLength || 800; // Reasonable default for mobile cameras

    // Initialize pose estimator
    this.posit = new jsAruco.POS1.Posit(this.markerSizeMm, this.focalLength);
  }

  /**
   * Detect markers in an image
   * @param imageData - Canvas ImageData or similar structure
   * @returns Array of detected markers with computed properties
   */
  detect(imageData: ImageData): DetectedMarker[] {
    const rawMarkers = this.detector.detect(imageData);

    return rawMarkers.map((marker: JsArucoMarker) => this.processMarker(marker));
  }

  /**
   * Detect markers from a video element
   * @param video - Video element to capture frame from
   * @returns Array of detected markers
   */
  detectFromVideo(video: HTMLVideoElement): DetectedMarker[] {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return [];

    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    return this.detect(imageData);
  }

  /**
   * Detect markers from a canvas
   * @param canvas - Canvas element to read from
   * @returns Array of detected markers
   */
  detectFromCanvas(canvas: HTMLCanvasElement): DetectedMarker[] {
    const ctx = canvas.getContext('2d');
    if (!ctx) return [];

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return this.detect(imageData);
  }

  /**
   * Estimate pose for a detected marker
   * @param marker - Detected marker
   * @param imageWidth - Image width for coordinate normalization
   * @param imageHeight - Image height for coordinate normalization
   * @returns Pose estimation or null if failed
   */
  estimatePose(marker: DetectedMarker, imageWidth: number, imageHeight: number): MarkerPose | null {
    if (!this.posit) return null;

    try {
      // Convert corners to centered coordinates (origin at image center)
      const centeredCorners = marker.corners.map(c => ({
        x: c.x - imageWidth / 2,
        y: imageHeight / 2 - c.y, // Flip Y for standard coordinate system
      }));

      const pose = this.posit.pose(centeredCorners);

      // Extract Euler angles from rotation matrix
      const R = pose.bestRotation;
      const euler = this.rotationMatrixToEuler(R);

      return {
        position: {
          x: pose.bestTranslation[0],
          y: pose.bestTranslation[1],
          z: pose.bestTranslation[2],
        },
        rotation: euler,
        rotationMatrix: R,
      };
    } catch {
      console.warn('[ArucoDetector] Pose estimation failed');
      return null;
    }
  }

  /**
   * Update camera focal length (call when camera resolution changes)
   */
  setFocalLength(focalLength: number): void {
    this.focalLength = focalLength;
    this.posit = new jsAruco.POS1.Posit(this.markerSizeMm, this.focalLength);
  }

  /**
   * Set physical marker size
   */
  setMarkerSize(sizeMm: number): void {
    this.markerSizeMm = sizeMm;
    this.posit = new jsAruco.POS1.Posit(this.markerSizeMm, this.focalLength);
  }

  // Private methods

  private processMarker(marker: JsArucoMarker): DetectedMarker {
    const corners = marker.corners as MarkerCorner[];

    // Compute center
    const center = {
      x: corners.reduce((sum, c) => sum + c.x, 0) / 4,
      y: corners.reduce((sum, c) => sum + c.y, 0) / 4,
    };

    // Compute approximate size (average of diagonals)
    const diag1 = Math.hypot(
      corners[2].x - corners[0].x,
      corners[2].y - corners[0].y
    );
    const diag2 = Math.hypot(
      corners[3].x - corners[1].x,
      corners[3].y - corners[1].y
    );
    const size = (diag1 + diag2) / 2 / Math.SQRT2; // Convert diagonal to side

    // Compute rotation from top edge
    const topEdge = {
      x: corners[1].x - corners[0].x,
      y: corners[1].y - corners[0].y,
    };
    const rotation = Math.atan2(topEdge.y, topEdge.x);

    return {
      id: marker.id,
      corners,
      center,
      size,
      rotation,
    };
  }

  private rotationMatrixToEuler(R: number[][]): { x: number; y: number; z: number } {
    // Extract Euler angles (XYZ order) from rotation matrix
    let x: number, y: number, z: number;

    if (Math.abs(R[2][0]) < 0.9999) {
      y = Math.asin(-R[2][0]);
      x = Math.atan2(R[2][1], R[2][2]);
      z = Math.atan2(R[1][0], R[0][0]);
    } else {
      // Gimbal lock
      z = 0;
      if (R[2][0] < 0) {
        y = Math.PI / 2;
        x = Math.atan2(R[0][1], R[0][2]);
      } else {
        y = -Math.PI / 2;
        x = Math.atan2(-R[0][1], -R[0][2]);
      }
    }

    return { x, y, z };
  }
}

/**
 * Create a singleton detector instance
 */
let detectorInstance: ArucoDetector | null = null;

export function getArucoDetector(config?: ArucoDetectorConfig): ArucoDetector {
  if (!detectorInstance) {
    detectorInstance = new ArucoDetector(config);
  }
  return detectorInstance;
}

/**
 * Check if ArUco detection is available
 */
export function isArucoAvailable(): boolean {
  return typeof jsAruco !== 'undefined' && jsAruco.AR && jsAruco.AR.Detector;
}
